/**
 * Browser identification, read client-side so it reflects the browser the desktop app actually
 * launched -- not whatever a proxy put in the User-Agent header.
 *
 * Total, like the query parser: a missing navigator, a frozen userAgentData, or a User-Agent
 * string in an unexpected shape must degrade to "unknown", never throw.
 */

export interface BrowserInfo {
  /** navigator.userAgent verbatim, or "" when unavailable. */
  userAgent: string;
  /** Friendly name, e.g. "Microsoft Edge". null when it cannot be determined reliably. */
  name: string | null;
  /** Version as reported, e.g. "141.0.3537.57". null when not determinable. */
  version: string | null;
  /** Where name/version came from -- userAgentData is authoritative, UA parsing is a guess. */
  source: "userAgentData" | "userAgent" | "unknown";
  platform: string | null;
  mobile: boolean | null;
  /** Full brand list from userAgentData, when the browser exposes it. */
  brands: string[];
  languages: string[];
}

/**
 * Ordered longest-match-first: Edge's UA contains "Chrome", Chrome's contains "Safari", and
 * Safari's contains "Version". Checking the most specific token first is what makes this
 * reliable rather than a coin flip.
 */
const UA_PATTERNS: ReadonlyArray<{ name: string; re: RegExp }> = [
  { name: "Microsoft Edge", re: /\bEdg(?:e|A|iOS)?\/([\d.]+)/ },
  { name: "Opera", re: /\bOPR\/([\d.]+)/ },
  { name: "Samsung Internet", re: /\bSamsungBrowser\/([\d.]+)/ },
  { name: "Vivaldi", re: /\bVivaldi\/([\d.]+)/ },
  { name: "Brave", re: /\bBrave\/([\d.]+)/ },
  { name: "Firefox", re: /\bFirefox\/([\d.]+)/ },
  { name: "Internet Explorer", re: /\bMSIE ([\d.]+)|\bTrident\/.*\brv:([\d.]+)/ },
  { name: "Chrome", re: /\bChrome\/([\d.]+)/ },
  { name: "Safari", re: /\bVersion\/([\d.]+).*\bSafari\// },
];

/**
 * Chromium sends a deliberate junk ("GREASE") brand to stop naive code keying off brand order.
 * Its punctuation is randomised on purpose and has included "Not A;Brand", "Not;A=Brand",
 * "Not)A;Brand", "Not.A/Brand" and more -- so match on letters alone rather than chasing the
 * separator of the month.
 */
function isGreaseBrand(brand: string): boolean {
  return brand.replace(/[^a-z0-9]/gi, "").toLowerCase() === "notabrand";
}

/** "Chromium" is the engine every Chromium browser also claims; a sibling brand names the app. */
const ENGINE_BRAND = "chromium";

export function readBrowserInfo(): BrowserInfo {
  const nav: any = typeof navigator === "undefined" ? undefined : navigator;
  const userAgent = safe(() => String(nav?.userAgent ?? "")) ?? "";

  const uaData = safe(() => nav?.userAgentData) ?? undefined;
  const brandList: Array<{ brand: string; version: string }> =
    safe(() => (Array.isArray(uaData?.brands) ? uaData.brands : [])) ?? [];
  const brands = brandList.map((b) => `${safeText(b?.brand)} ${safeText(b?.version)}`.trim()).filter(Boolean);

  // Prefer userAgentData: it is structured and, unlike the UA string, not frozen/spoofed by default.
  // Edge reports [GREASE, "Chromium", "Microsoft Edge"], so skip both the junk brand and the
  // engine to land on the product name; fall back to Chromium when that is all there is.
  const realBrands = brandList.filter((b) => typeof b?.brand === "string" && !isGreaseBrand(b.brand));
  const realBrand =
    realBrands.find((b) => b.brand.trim().toLowerCase() !== ENGINE_BRAND) ?? realBrands[0];
  if (realBrand) {
    return {
      userAgent,
      name: safeText(realBrand.brand) || null,
      version: safeText(realBrand.version) || null,
      source: "userAgentData",
      platform: safeText(uaData?.platform) || null,
      mobile: typeof uaData?.mobile === "boolean" ? uaData.mobile : null,
      brands,
      languages: readLanguages(nav),
    };
  }

  for (const { name, re } of UA_PATTERNS) {
    const match = safe(() => re.exec(userAgent)) ?? null;
    if (match) {
      return {
        userAgent,
        name,
        // IE's alternation puts the version in group 1 or group 2 depending on which arm matched.
        version: match[1] ?? match[2] ?? null,
        source: "userAgent",
        platform: safeText(nav?.platform) || null,
        mobile: /\bMobi\b|\bAndroid\b|\biPhone\b/.test(userAgent) || null,
        brands,
        languages: readLanguages(nav),
      };
    }
  }

  return {
    userAgent,
    name: null,
    version: null,
    source: "unknown",
    platform: safeText(nav?.platform) || null,
    mobile: null,
    brands,
    languages: readLanguages(nav),
  };
}

function readLanguages(nav: any): string[] {
  const list = safe(() => (Array.isArray(nav?.languages) ? nav.languages : [nav?.language])) ?? [];
  return list.map(safeText).filter(Boolean);
}

function safeText(value: unknown): string {
  try {
    return typeof value === "string" ? value : value == null ? "" : String(value);
  } catch {
    return "";
  }
}

function safe<T>(read: () => T): T | undefined {
  try {
    return read();
  } catch {
    return undefined;
  }
}
